import type { PersonHref } from '@generalTypes/apiTypes';
import type { Person } from '@generalTypes/personApiTypes';
import type { RootState } from '@generalTypes/rootStateTypes';
import { selectPersons } from '@newStore/externalData/externalDataSelectors';
import { createTypedSelector } from '@newStore/genericHelpers';
import type { NodeType } from '@nodeTypeConfig/configTypes';
import { settings } from '../../config/settings';
import {
  type NodeTypeSecurityMap,
  getAllowedTypes,
  getNodeTypeSecurityMap,
  getUrlsForPath,
} from './userHelpers';

const emptyArray = [];

export const selectSecurityData = (state: RootState): Record<string, string[]> | null =>
  state.newUser.securityData;

export const selectUser = (state: RootState): Person | null =>
  state.newUser.personHref ? selectPersons(state)[state.newUser.personHref] : null;

export const selectUserHref = (state: RootState): PersonHref | null =>
  state.newUser.personHref as PersonHref;

export const selectIsUserLoaded = (state: RootState): boolean =>
  state.newUser.isLoading === false && selectUser(state) !== undefined;

export const selectAllowedAbilities = createTypedSelector(
  [selectSecurityData],
  (securityData: Record<string, string[]> | null): string[] => {
    if (!securityData) {
      return [];
    }
    const allowedAbilities: string[] = [];

    settings.securityModes.forEach((mode) => {
      if (mode.rawUrl && securityData[mode.type].includes(mode.rawUrl)) {
        allowedAbilities.push(mode.type);
      }
    });

    if (
      getUrlsForPath(securityData.UPDATE_CONTENT, '/content/hashes').some((rawUrl) => {
        const keys = rawUrl.searchParams.get('keyIn')?.split(',') || [];
        return keys?.includes(settings.newsHash);
      })
    ) {
      allowedAbilities.push('UPDATE_NEWS');
    }

    return allowedAbilities;
  },
);

export const selectHasUserReviewAbility = (state: RootState) =>
  selectAllowedAbilities(state).includes('REVIEW');

export const selectHasUserUpdateNewsAbility = (state: RootState) =>
  selectAllowedAbilities(state).includes('UPDATE_NEWS');

export const selectHasUserSuggestAbility = (state: RootState) =>
  selectAllowedAbilities(state).includes('SUGGEST');

export const selectCanUserPersistSnapshot = createTypedSelector(
  [selectSecurityData],
  (securityData): boolean => {
    if (!securityData) {
      return false;
    }
    const snapshotResources: string[] = securityData.SNAPSHOT ?? securityData.snapshot ?? [];
    return getUrlsForPath(snapshotResources, '/documents').length > 0;
  },
);

export const selectSecurityUserRoles = (state: RootState) => state.newUser.userRoles;

export const selectPublishedEditableTypes = createTypedSelector(
  [(state) => selectSecurityData(state)],
  (securityData: Record<string, string[]> | null): NodeType[] => {
    if (!securityData) {
      return emptyArray;
    }

    const allowedTypes = getAllowedTypes(securityData);

    return allowedTypes.filter((t) => t.isPublishedEditable).map((z) => z.type);
  },
);

export const selectNodeTypeSecurityMap = createTypedSelector(
  [
    (state) => selectSecurityData(state),
    selectHasUserSuggestAbility,
    selectHasUserReviewAbility,
    selectSecurityUserRoles,
  ],
  (securityData, canSuggest, canReview, userRoles): NodeTypeSecurityMap => {
    if (!securityData) {
      return {};
    }

    const result = getNodeTypeSecurityMap(securityData, canSuggest, canReview, userRoles);
    console.log('NodeTypeSecurityMap', result);
    return result;
  },
);
